import {gql} from '@apollo/client'
import React, {useEffect} from 'react'
import {usePrompt} from 'react-router-dom'
import {ButtonGroup, FormRow} from '../../components/form'
import {Button} from '../../components/library/buttons'
import {TextInput} from '../../components/library/form'
import {Loader} from '../../components/loader'
import {useGetCurrentAccountQuery} from '../../data/workbench-api/generated/account'
import {useStateChanged} from '../../hooks/use-state-changed'
import {useAnalytics} from '../../lib/analytics/analytics'
import {getClient} from '../../lib/api'
import {coerceString} from '../../lib/helpers'
import {useToast} from '../../lib/toast'
import {
  BusinessUserProfileFragment,
  SaveBusinessUserProfileMutationVariables,
  useBusinessUserProfileQuery,
  useSaveBusinessUserProfileMutation
} from './generated/profile-fields'

const isValid = (
  data: BusinessUserProfileFragment
): data is SaveBusinessUserProfileMutationVariables => {
  return !!(
    data.firstName &&
    data.lastName &&
    data.accountId &&
    data.organisationId
  )
}

const withEmptyPhone = (bu: BusinessUserProfileFragment) => ({
  ...bu,
  phone: bu.phone ?? ''
})

export const ProfileFields = ({organisationId}: {organisationId: string}) => {
  const analytics = useAnalytics()
  const [state, changeState, changed, resetState] =
    useStateChanged<BusinessUserProfileFragment>({
      organisationId,
      accountId: -1,
      firstName: '',
      lastName: '',
      phone: ''
    })
  const {data, loading, refetch} = useBusinessUserProfileQuery({
    variables: {organisationId},
    client: getClient(organisationId)
  })
  const toast = useToast()
  usePrompt('You have unsaved changes are you sure you want to leave', changed)
  useEffect(() => {
    const businessUser = data?.getOrganisationAccess?.currentBusinessUser
    if (businessUser) {
      resetState(withEmptyPhone(businessUser))
    }
  }, [data, resetState])
  const [save, {loading: saving}] = useSaveBusinessUserProfileMutation({
    onCompleted: res => {
      if (res.updateOwnBusinessUserProfile?.businessUser) {
        const businessUser = res.updateOwnBusinessUserProfile.businessUser
        resetState(withEmptyPhone(businessUser))
        analytics.identify({
          traits: {
            first_name: coerceString(businessUser.firstName) ?? '',
            last_name: coerceString(businessUser.lastName) ?? '',
            email: coerceString(businessUser.email) ?? '',
            org_id: organisationId,
            org_name: coerceString(businessUser.organisation?.name) ?? ''
          }
        })
        analytics.track({
          event: 'Updated profile',
          properties: {
            category: 'general',
            org_id: organisationId,
            first_name: coerceString(businessUser.firstName) ?? '',
            last_name: coerceString(businessUser.lastName) ?? ''
          }
        })

        toast('Changes saved')
      }
    },
    onError: err => toast(err.message, 'error'),
    client: getClient(organisationId)
  })
  const valid = state.firstName && state.lastName

  const userEmail = useGetCurrentAccountQuery()?.data?.getCurrentAccount?.email

  return loading ? (
    <Loader />
  ) : (
    <form
      onSubmit={async e => {
        e.preventDefault()
        if (isValid(state)) {
          await save({
            variables: state
          })
          // if the user subsequently changes and then resets we want the latest state
          refetch()
        }
      }}
    >
      <FormRow>
        <TextInput
          type="text"
          label="First name"
          required={true}
          onChange={firstName => {
            changeState(s => ({...s, firstName}))
          }}
          value={state.firstName ?? ''}
          style={{maxWidth: 'none'}}
        />
      </FormRow>
      <FormRow>
        <TextInput
          type="text"
          label="Last name"
          required={true}
          onChange={lastName => {
            changeState(s => ({...s, lastName}))
          }}
          value={state.lastName ?? ''}
          style={{maxWidth: 'none'}}
        />
      </FormRow>
      <FormRow>
        <TextInput
          type="text"
          label="Phone"
          onChange={phone => {
            changeState(s => ({...s, phone}))
          }}
          value={state.phone ?? ''}
          style={{maxWidth: 'none'}}
        />
      </FormRow>
      <FormRow>
        <TextInput
          readOnly
          type="text"
          label="Email"
          value={userEmail ?? ''}
          onChange={() => {
            // no-op
          }}
          style={{maxWidth: 'none'}}
        />
      </FormRow>
      {changed && (
        <ButtonGroup>
          <Button type="submit" size="large" disabled={!valid || saving}>
            Save
          </Button>
          <Button
            size="small"
            appearance="secondary"
            onClick={() => {
              resetState()
            }}
          >
            Discard changes
          </Button>
        </ButtonGroup>
      )}
    </form>
  )
}

gql`
  fragment BusinessUserProfile on BusinessUser {
    accountId
    organisationId
    firstName
    lastName
    phone
    email
    organisation {
      name
    }
  }

  query BusinessUserProfile($organisationId: String!) {
    getOrganisationAccess(organisationId: $organisationId) {
      currentBusinessUser {
        ...BusinessUserProfile
      }
    }
  }

  mutation SaveBusinessUserProfile(
    $accountId: Int!
    $organisationId: String!
    $firstName: String!
    $lastName: String!
    $phone: String!
  ) {
    updateOwnBusinessUserProfile(
      input: {
        accountId: $accountId
        organisationId: $organisationId
        firstName: $firstName
        lastName: $lastName
        phone: $phone
      }
    ) {
      businessUser {
        ...BusinessUserProfile
      }
    }
  }
`
